import { useEffect, useRef } from 'react'

/**
 * Llama a `onRefetch` cuando:
 * 1. La pestaña vuelve a tener foco (window.focus)
 * 2. La pestaña vuelve a ser visible (visibilitychange → visible)
 * 3. Se reconecta el WebSocket (callback `onCableReconnect` opcional)
 *
 * Esto resuelve el caso de:
 * - Cliente apaga el cel
 * - Cambia a otra app/pestaña
 * - Conexión WiFi se cae
 * ...y al volver, los datos del carrito y stock están frescos.
 *
 * @param onRefetch Función a ejecutar (no debe cambiar de referencia — usar useCallback)
 * @param options.debounceMs Cuánto esperar antes de llamar (evita doble-fetch en mobile)
 */
export function useFocusRefetch(
  onRefetch: () => void,
  options: { debounceMs?: number } = {}
) {
  const { debounceMs = 300 } = options
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const trigger = () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        onRefetch()
        timerRef.current = null
      }, debounceMs)
    }

    const onFocus = () => trigger()
    const onVisibility = () => {
      if (document.visibilityState === 'visible') trigger()
    }

    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [onRefetch, debounceMs])
}
